import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  User, ChevronDown, ChevronUp, FileText, PenTool, Download,
  CheckCircle, Eye, Upload, AlertTriangle, ClipboardList, Award,
  Lock, Save, Phone, Calendar, FileCheck, TrendingUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getAuthPhotoUrl } from '../AuthImage';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import EPMSPConsultation from '../EPMSPConsultation';
import CompetencesPrerequisConsultation from '../CompetencesPrerequisConsultation';

const COLORS = [
  { border: 'border-l-[#8DC63F]', bg: 'bg-[#f7fdf0]', badge: 'bg-[#8DC63F]' },
  { border: 'border-l-[#3B82F6]', bg: 'bg-[#eff6ff]', badge: 'bg-[#3B82F6]' },
  { border: 'border-l-[#F59E0B]', bg: 'bg-[#fffbeb]', badge: 'bg-[#F59E0B]' },
  { border: 'border-l-[#EC4899]', bg: 'bg-[#fdf2f8]', badge: 'bg-[#EC4899]' },
  { border: 'border-l-[#8B5CF6]', bg: 'bg-[#f5f3ff]', badge: 'bg-[#8B5CF6]' },
];

const getNoteColor = (val) => {
  if (val === 0) return 'bg-red-100 text-red-700 border-red-300';
  if (val === 1) return 'bg-orange-100 text-orange-700 border-orange-300';
  if (val === 2) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  if (val === 3) return 'bg-green-100 text-green-700 border-green-300';
  return 'bg-slate-50 text-slate-400 border-slate-200';
};

const StagiaireAccordionCardTuteur = ({
  stagiaire, index, convention, grilleStructure,
  onSignConvention, onViewConvention, onDownloadConvention,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('profil');
  const [profile, setProfile] = useState(null);
  const [allDocs, setAllDocs] = useState([]);
  const [certData, setCertData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingEpmsp, setUploadingEpmsp] = useState(false);
  const [compStats, setCompStats] = useState(null);
  const [hasDeclaration, setHasDeclaration] = useState(false);

  // Grille state
  const [grilleData, setGrilleData] = useState({ evaluations: {}, commentaire_global: '' });
  const [grilleLoaded, setGrilleLoaded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [savingGrille, setSavingGrille] = useState(false);

  const color = COLORS[index % COLORS.length];
  const sId = stagiaire.user_id;
  const initials = `${(stagiaire.user_info?.prenom || '')[0] || ''}${(stagiaire.user_info?.nom || '')[0] || ''}`.toUpperCase();
  const stagiaireNom = `${stagiaire.user_info?.prenom || ''} ${stagiaire.user_info?.nom || ''}`;
  const needsConventionSignature = convention &&
    (convention.statut === 'en_attente_signatures' || convention.statut === 'partiellement_signe') &&
    !convention.signature_tuteur;
  const conventionValidated = convention && convention.statut === 'signe';

  useEffect(() => {
    if (isExpanded && !profile && !loading) loadExpandedData();
  }, [isExpanded]);

  // Load competences count and declaration status on mount
  useEffect(() => {
    axios.get(`/competences/stagiaire/${sId}`).then(res => {
      const comps = res.data || [];
      const acquired = comps.filter(c => c.statut === 'competent').length;
      setCompStats({ acquired, total: comps.length });
    }).catch(() => {});
    axios.get(`/documents/${sId}`).then(res => {
      const docs = res.data || [];
      setHasDeclaration(docs.some(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp'));
    }).catch(() => {});
  }, [sId]);

  useEffect(() => {
    if (activeTab === 'grille' && !grilleLoaded && grilleStructure) loadGrille();
  }, [activeTab, grilleStructure]);

  const loadExpandedData = async () => {
    setLoading(true);
    try {
      const [stagRes, docsRes] = await Promise.all([
        axios.get(`/stagiaires/${sId}`),
        axios.get(`/documents/${sId}`)
      ]);
      setProfile(stagRes.data);
      setAllDocs(docsRes.data || []);
      try {
        const statusRes = await axios.get(`/epmsp/certificat-status/${sId}`);
        if (statusRes.data.all_validated && statusRes.data.certificat_sent) {
          const docs = docsRes.data || [];
          setCertData({
            ...statusRes.data,
            declaration_doc: docs.find(d => d.document_type === 'declaration_epmsp'),
            attestation_doc: docs.find(d => d.document_type === 'attestation_epmsp'),
          });
        }
      } catch {}
    } catch (e) { console.error('Error loading data:', e); }
    setLoading(false);
  };

  const loadGrille = async () => {
    try {
      const res = await axios.get(`/grille-evaluation/${sId}`);
      setGrilleData(res.data || { evaluations: {}, commentaire_global: '' });
      setGrilleLoaded(true);
    } catch { setGrilleLoaded(true); }
  };

  const handleGrilleChange = (critereKey, periodeId, value) => {
    setGrilleData(prev => {
      const evals = { ...prev.evaluations };
      if (!evals[critereKey]) evals[critereKey] = {};
      evals[critereKey][periodeId] = value;
      return { ...prev, evaluations: evals };
    });
  };

  const handleSaveGrille = async () => {
    setSavingGrille(true);
    try {
      await axios.post(`/grille-evaluation/${sId}`, {
        evaluations: grilleData.evaluations,
        commentaire_global: grilleData.commentaire_global
      });
      toast.success("Grille d'évaluation sauvegardée !");
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    setSavingGrille(false);
  };

  const handleViewDoc = async (docId) => {
    try {
      const res = await axios.get(`/candidats/doc-view/${docId}`, { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] })), '_blank');
    } catch {
      try {
        const res = await axios.get(`/documents/download/${docId}`, { responseType: 'blob' });
        window.open(window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] })), '_blank');
      } catch { toast.error('Erreur lors de la visualisation'); }
    }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`/documents/download/${docId}`);
      if (res.data.file_data) {
        const byteChars = atob(res.data.file_data);
        const byteNums = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
        const blob = new Blob([new Uint8Array(byteNums)], { type: res.data.content_type || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || 'document.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch { toast.error('Erreur lors du téléchargement'); }
  };

  const handleUploadEpmsp = async (file) => {
    if (!file) return;
    setUploadingEpmsp(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', sId);
      formData.append('document_type', 'declaration_epmsp');
      await axios.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Déclaration stagiaire uploadée !');
      const docsRes = await axios.get(`/documents/${sId}`);
      setAllDocs(docsRes.data || []);
    } catch { toast.error("Erreur lors de l'upload"); }
    setUploadingEpmsp(false);
  };

  const epmspDoc = allDocs.find(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp');
  const lockedPeriodes = grilleStructure?.locked_periodes || [];

  return (
    <div className={`${color.bg} border border-slate-200 ${color.border} border-l-4 rounded-lg shadow-sm transition-all hover:shadow-md`} data-testid={`stagiaire-card-${sId}`}>
      {/* Header */}
      <div className="p-4 cursor-pointer select-none" onClick={() => setIsExpanded(!isExpanded)} data-testid={`stagiaire-header-${sId}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4 min-w-0">
            {(stagiaire.user_info?.photo_identite || stagiaire.user_info?.photo_url || stagiaire.photo_identite) ? (
              <img src={getAuthPhotoUrl(stagiaire.user_info?.photo_identite || stagiaire.user_info?.photo_url || stagiaire.photo_identite)}
                alt={stagiaireNom} className="w-11 h-11 rounded-full object-cover shadow-sm flex-shrink-0 border-2 border-white" data-testid={`stagiaire-photo-${sId}`} />
            ) : (
              <div className={`w-11 h-11 ${color.badge} rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm flex-shrink-0`}>
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-[#0F172A] text-base">{stagiaireNom}</p>
                {needsConventionSignature && <Badge className="bg-orange-500 text-white text-[10px] px-1.5 py-0">Convention à signer</Badge>}
                {conventionValidated && <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0">Convention validée</Badge>}
                {hasDeclaration && <Badge className="bg-blue-600 text-white text-[10px] px-1.5 py-0" data-testid={`badge-declare-${sId}`}>Stagiaire déclaré</Badge>}
                {compStats && compStats.total > 0 && (
                  <Badge className={`text-[10px] px-1.5 py-0 ${compStats.acquired === compStats.total ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-700'}`} data-testid={`badge-comp-${sId}`}>
                    {compStats.acquired}/{compStats.total} compétences acquises
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-500">{stagiaire.centre_formation} — {stagiaire.statut}</p>
              {stagiaire.type_contrat && (
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                  {stagiaire.type_contrat === 'stagiaire_fp' ? 'Stagiaire formation pro.' :
                   stagiaire.type_contrat === 'apprenti' ? 'Apprenti' :
                   stagiaire.type_contrat === 'contrat_pro' ? 'Contrat pro.' :
                   stagiaire.type_contrat === 'salarie' ? 'Salarié' : stagiaire.type_contrat}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <Link to={`/progression?stagiaire=${sId}`} className="px-3 py-1.5 bg-[#8DC63F] text-white rounded-lg text-sm font-medium hover:bg-[#7db336] transition-colors flex items-center gap-1" data-testid={`btn-progression-${sId}`}>
              <TrendingUp className="h-4 w-4" /> Progression
            </Link>
            <button
              onClick={() => { setIsExpanded(true); setActiveTab('grille'); }}
              className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors flex items-center gap-1 animate-pulse"
              data-testid={`btn-comp-generales-${sId}`}
            >
              <ClipboardList className="h-4 w-4" /> Évaluer Comp. Générales
            </button>
            <Link to="/competences" className="px-3 py-1.5 border border-blue-500 text-blue-500 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-1" data-testid={`btn-comp-pratiques-${sId}`}>
              <Award className="h-4 w-4" /> Évaluer Comp. Pratiques
            </Link>
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-2 rounded-lg hover:bg-white/60 transition-colors" data-testid={`btn-expand-${sId}`}>
              {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-200/60">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]" />
              <span className="ml-3 text-sm text-slate-500">Chargement...</span>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-3">
              <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-slate-100/80 p-1 rounded-lg">
                <TabsTrigger value="profil" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1" data-testid={`tab-profil-${sId}`}>
                  <User className="h-3.5 w-3.5" /> Profil
                </TabsTrigger>
                <TabsTrigger value="grille" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1" data-testid={`tab-grille-${sId}`}>
                  <ClipboardList className="h-3.5 w-3.5" /> Grille
                </TabsTrigger>
                <TabsTrigger value="convention" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1 relative" data-testid={`tab-convention-${sId}`}>
                  <PenTool className="h-3.5 w-3.5" /> Convention
                  {needsConventionSignature && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-orange-500 rounded-full" />}
                </TabsTrigger>
                <TabsTrigger value="competences" className="flex-1 min-w-[80px] text-xs sm:text-sm gap-1" data-testid={`tab-competences-${sId}`}>
                  <CheckCircle className="h-3.5 w-3.5" /> Compétences acquises
                </TabsTrigger>
              </TabsList>

              {/* TAB: Profil */}
              <TabsContent value="profil" className="mt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                      <User className="h-4 w-4 text-green-600" /> Informations personnelles
                    </h4>
                    <div className="bg-white rounded-lg p-4 space-y-2 text-sm border border-slate-100">
                      <p><span className="text-slate-500 w-28 inline-block">Email :</span> <span className="font-medium">{profile?.user_info?.email || '-'}</span></p>
                      <p><span className="text-slate-500 w-28 inline-block">Téléphone :</span> <span className="font-medium">{profile?.user_info?.telephone || profile?.telephone || '-'}</span></p>
                      <p><span className="text-slate-500 w-28 inline-block">Date naiss. :</span> <span className="font-medium">{profile?.date_naissance || '-'}</span></p>
                      <p><span className="text-slate-500 w-28 inline-block">Gr. sanguin :</span> <span className="font-medium">{profile?.groupe_sanguin || '-'}</span></p>
                    </div>
                    <div className="bg-red-50 rounded-lg border border-red-200 p-4 space-y-2 text-sm">
                      <h5 className="font-medium text-red-700 flex items-center gap-1 text-xs"><AlertTriangle className="h-3 w-3" /> Contact d'urgence</h5>
                      <p><span className="text-slate-500 w-28 inline-block">Nom :</span> <span className="font-medium">{profile?.contact_urgence_nom || '-'}</span></p>
                      <p><span className="text-slate-500 w-28 inline-block">Tél :</span> <span className="font-medium">{profile?.contact_urgence_telephone || '-'}</span></p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* TAB: Grille d'évaluation */}
              <TabsContent value="grille" className="mt-3">
                {!grilleStructure ? (
                  <p className="text-sm text-slate-500 text-center py-4">Grille non disponible</p>
                ) : (
                  <div>
                    <div className="flex flex-wrap items-center gap-3 py-3 text-xs">
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded">0 = Non intégré</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">1 = Début d'acquisition</span>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">2 = En cours</span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">3 = Acquis</span>
                      {lockedPeriodes.length > 0 && (
                        <span className="px-2 py-1 bg-slate-200 text-slate-600 rounded flex items-center gap-1">
                          <Lock className="h-3 w-3" /> = Verrouillé
                        </span>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse bg-white rounded-lg overflow-hidden">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="text-left p-2 border border-slate-200 min-w-[280px]">Critère</th>
                            {grilleStructure.periodes.map(p => {
                              const isLocked = lockedPeriodes.includes(p.id);
                              return (
                                <th key={p.id} className={`p-2 border border-slate-200 text-center min-w-[70px] ${isLocked ? 'bg-slate-200' : ''}`}>
                                  <div className="text-xs font-bold flex items-center justify-center gap-1">
                                    {isLocked && <Lock className="h-3 w-3 text-slate-500" />}
                                    {p.mois}
                                  </div>
                                  <div className="text-xs text-slate-500">{p.label}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {grilleStructure.sections.map(section => {
                            const sectionKey = `${sId}_section_${section.id}`;
                            const isSectionExpanded = expandedSections[sectionKey] !== false;
                            return (
                              <React.Fragment key={`section-${section.id}`}>
                                <tr className="cursor-pointer" onClick={() => setExpandedSections(prev => ({ ...prev, [sectionKey]: !isSectionExpanded }))}>
                                  <td colSpan={grilleStructure.periodes.length + 1} className="p-2 bg-amber-50 border border-slate-200 font-bold text-amber-800 text-xs uppercase tracking-wide">
                                    <div className="flex items-center justify-between">
                                      <span>{section.titre}</span>
                                      {isSectionExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </div>
                                  </td>
                                </tr>
                                {isSectionExpanded && section.criteres.map((critere, idx) => {
                                  const critereKey = `${section.id}_${idx}`;
                                  return (
                                    <tr key={critereKey} className="hover:bg-slate-50">
                                      <td className="p-2 border border-slate-200 text-xs text-slate-700">{critere}</td>
                                      {grilleStructure.periodes.map(p => {
                                        const val = grilleData.evaluations?.[critereKey]?.[p.id];
                                        const isLocked = lockedPeriodes.includes(p.id);
                                        return (
                                          <td key={p.id} className={`p-1 border border-slate-200 text-center ${isLocked ? 'bg-slate-100' : ''}`}>
                                            <select
                                              value={val !== undefined && val !== null ? val : ''}
                                              onChange={(e) => handleGrilleChange(critereKey, p.id, e.target.value === '' ? null : parseInt(e.target.value))}
                                              disabled={isLocked}
                                              className={`w-full text-center text-xs p-1 rounded border ${isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${val !== undefined && val !== null ? getNoteColor(val) : 'bg-white border-slate-200'}`}
                                            >
                                              <option value="">-</option>
                                              <option value="0">0 — Non intégré</option>
                                              <option value="1">1 — Début d'acquisition</option>
                                              <option value="2">2 — En cours</option>
                                              <option value="3">3 — Acquis</option>
                                            </select>
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4">
                      <label className="text-sm font-medium text-slate-700 block mb-1">Commentaire global</label>
                      <textarea
                        value={grilleData.commentaire_global || ''}
                        onChange={(e) => setGrilleData(prev => ({ ...prev, commentaire_global: e.target.value }))}
                        className="w-full border border-slate-200 rounded-lg p-2 text-sm resize-none h-20"
                        placeholder="Remarques générales..."
                        data-testid={`grille-comment-${sId}`}
                      />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button onClick={handleSaveGrille} disabled={savingGrille} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid={`grille-save-${sId}`}>
                        <Save className="h-4 w-4 mr-2" />{savingGrille ? 'Sauvegarde...' : 'Sauvegarder la grille'}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* TAB: Convention */}
              <TabsContent value="convention" className="mt-3">
                {!convention ? (
                  <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
                    <PenTool className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Aucune convention en cours</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-sm text-slate-600">Formation : {convention.date_debut_formation} au {convention.date_fin_formation}</p>
                        {convention.periodes_stage?.length > 0 && (
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {convention.periodes_stage.length} période(s) de stage
                          </p>
                        )}
                        <div className="mt-2">
                          <Badge className={`${
                            convention.statut === 'signe' ? 'bg-green-100 text-green-700 border border-green-200' :
                            convention.statut === 'en_attente_validation_centre' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                            needsConventionSignature ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                            'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}>
                            {convention.statut === 'signe' ? 'Signée et validée' :
                             convention.statut === 'en_attente_validation_centre' ? 'En attente de validation du centre' :
                             needsConventionSignature ? 'En attente de votre signature' :
                             'Signatures en cours'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => (onViewConvention || onDownloadConvention)(sId, `${stagiaire.prenom || ''}_${stagiaire.nom || ''}`)} data-testid={`btn-view-conv-${sId}`}>
                          <Eye className="h-4 w-4 mr-1" /> Consulter
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => onDownloadConvention(sId, `${stagiaire.prenom || ''}_${stagiaire.nom || ''}`)} data-testid={`btn-dl-conv-${sId}`}>
                          <Download className="h-4 w-4 mr-1" /> Télécharger
                        </Button>
                        {needsConventionSignature && (
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => onSignConvention(convention)} data-testid={`btn-sign-conv-${sId}`}>
                            <PenTool className="h-4 w-4 mr-1" /> Signer
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* TAB: Compétences (inclut EPMSP, Prérequis, Blocs) */}
              <TabsContent value="competences" className="mt-3">
                <CompetencesPrerequisConsultation stagiaireId={sId} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      )}
    </div>
  );
};

export default StagiaireAccordionCardTuteur;
